import { useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { Plus, Pencil, Trash2, Send, Archive } from 'lucide-react';
import {
  LMS_COURSE_STATUSES,
  LmsCourse,
  LmsCourseStatus,
  LmsCoursesApi,
} from '@/services/lms.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { DataTable } from '@/components/ui/DataTable';
import { Pagination } from '@/components/ui/Pagination';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Field, Input, Select, Textarea } from '@/components/ui/Input';
import { formatDate } from '@/lib/format';
import { toast } from '@/stores/toast.store';
import { usePermissions } from '@/hooks/usePermissions';

const statusTone: Record<LmsCourseStatus, 'slate' | 'green' | 'amber'> = {
  draft: 'slate',
  published: 'green',
  archived: 'amber',
};

const schema = z.object({
  title: z.string().min(1, 'Required').max(200),
  description: z.string().optional().or(z.literal('')),
  subject: z.string().max(100, 'Too long').optional().or(z.literal('')),
});
type FormValues = z.infer<typeof schema>;

export function LmsCoursesPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { isAdmin } = usePermissions();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [statusFilter, setStatusFilter] = useState<LmsCourseStatus | ''>('');

  const { data: pageData, isLoading } = useQuery({
    queryKey: ['lms-courses', page, limit, statusFilter],
    queryFn: () => LmsCoursesApi.list({ page, limit, status: statusFilter || undefined }),
    placeholderData: keepPreviousData,
  });

  const [modal, setModal] = useState<{ open: boolean; course?: LmsCourse }>({ open: false });
  const [confirmArchive, setConfirmArchive] = useState<{ open: boolean; course?: LmsCourse }>({ open: false });
  const [confirmDelete, setConfirmDelete] = useState<{ open: boolean; course?: LmsCourse }>({ open: false });

  const upsert = useMutation({
    mutationFn: (v: { id?: string; payload: FormValues }) =>
      v.id ? LmsCoursesApi.update(v.id, v.payload) : LmsCoursesApi.create(v.payload),
    onSuccess: (course, v) => {
      qc.invalidateQueries({ queryKey: ['lms-courses'] });
      setModal({ open: false });
      if (!v.id) {
        toast.success('Course created');
        navigate(`/lms/courses/${course.id}`);
      } else {
        toast.success('Course updated');
      }
    },
    onError: (e) => toast.error(errMsg(e) ?? 'Save failed'),
  });

  const publish = useMutation({
    mutationFn: (id: string) => LmsCoursesApi.publish(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lms-courses'] });
      toast.success('Course published');
    },
    onError: (e) => toast.error(errMsg(e) ?? 'Publish failed'),
  });

  const archive = useMutation({
    mutationFn: (id: string) => LmsCoursesApi.archive(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lms-courses'] });
      setConfirmArchive({ open: false });
      toast.success('Course archived');
    },
    onError: (e) => toast.error(errMsg(e) ?? 'Archive failed'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => LmsCoursesApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lms-courses'] });
      setConfirmDelete({ open: false });
      toast.success('Course deleted');
    },
    onError: (e) => toast.error(errMsg(e) ?? 'Delete failed'),
  });

  const courses = pageData?.items ?? [];

  return (
    <>
      <PageHeader
        title="LMS Courses"
        description="Build and publish online courses — modules, lessons, assignments, and quizzes."
        actions={
          <div className="flex items-center gap-2">
            <Select
              className="!w-44"
              value={statusFilter}
              onChange={(e) => {
                setPage(1);
                setStatusFilter(e.target.value as LmsCourseStatus | '');
              }}
            >
              <option value="">All statuses</option>
              {LMS_COURSE_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s[0].toUpperCase() + s.slice(1)}
                </option>
              ))}
            </Select>
            <button className="btn-primary" onClick={() => setModal({ open: true })}>
              <Plus className="mr-1.5 h-4 w-4" /> New course
            </button>
          </div>
        }
      />

      <DataTable<LmsCourse>
        rows={courses}
        getRowId={(r) => r.id}
        isLoading={isLoading}
        emptyMessage="No courses yet."
        columns={[
          {
            key: 'title',
            header: 'Title',
            render: (c) => (
              <button
                className="font-medium text-slate-900 hover:text-brand-700 hover:underline"
                onClick={() => navigate(`/lms/courses/${c.id}`)}
              >
                {c.title}
              </button>
            ),
          },
          { key: 'subject', header: 'Subject', render: (c) => c.subject ?? '—' },
          { key: 'status', header: 'Status', render: (c) => <Badge tone={statusTone[c.status]}>{c.status}</Badge> },
          { key: 'createdAt', header: 'Created', render: (c) => formatDate(c.createdAt) },
        ]}
        actions={(c) => (
          <>
            <button
              className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-brand-600"
              onClick={() => setModal({ open: true, course: c })}
              title="Edit"
            >
              <Pencil className="h-4 w-4" />
            </button>
            {c.status === 'draft' && (
              <button
                className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-green-600 disabled:opacity-40"
                onClick={() => publish.mutate(c.id)}
                disabled={publish.isPending}
                title="Publish"
              >
                <Send className="h-4 w-4" />
              </button>
            )}
            {c.status !== 'archived' && (
              <button
                className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-amber-600"
                onClick={() => setConfirmArchive({ open: true, course: c })}
                title="Archive"
              >
                <Archive className="h-4 w-4" />
              </button>
            )}
            {isAdmin && (
              <button
                className="rounded-md p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600"
                onClick={() => setConfirmDelete({ open: true, course: c })}
                title="Delete"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </>
        )}
      />

      {pageData && (
        <Pagination
          page={pageData.page}
          totalPages={pageData.totalPages}
          total={pageData.total}
          limit={pageData.limit}
          onPageChange={setPage}
          onLimitChange={(l) => {
            setPage(1);
            setLimit(l);
          }}
        />
      )}

      {modal.open && (
        <CourseFormModal
          open={modal.open}
          course={modal.course}
          onClose={() => setModal({ open: false })}
          saving={upsert.isPending}
          onSubmit={(v) => upsert.mutate({ id: modal.course?.id, payload: v })}
        />
      )}

      <ConfirmDialog
        open={confirmArchive.open}
        onClose={() => setConfirmArchive({ open: false })}
        onConfirm={() => confirmArchive.course && archive.mutate(confirmArchive.course.id)}
        loading={archive.isPending}
        title="Archive course?"
        message={`"${confirmArchive.course?.title}" will drop out of the student portal and stop accepting new enrollments. Existing enrollments are unaffected.`}
        confirmText="Archive"
        destructive={false}
      />

      <ConfirmDialog
        open={confirmDelete.open}
        onClose={() => setConfirmDelete({ open: false })}
        onConfirm={() => confirmDelete.course && remove.mutate(confirmDelete.course.id)}
        loading={remove.isPending}
        title="Delete course?"
        message={`Soft-delete "${confirmDelete.course?.title}". Its modules and lessons remain in the database.`}
        confirmText="Delete course"
      />
    </>
  );
}

export function CourseFormModal({
  open,
  course,
  onClose,
  onSubmit,
  saving,
}: {
  open: boolean;
  course?: LmsCourse;
  onClose: () => void;
  onSubmit: (v: FormValues) => void;
  saving: boolean;
}) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    values: {
      title: course?.title ?? '',
      description: course?.description ?? '',
      subject: course?.subject ?? '',
    },
  });

  // Backend's optional fields use @Length(1,100)/etc, which @IsOptional()
  // only skips for `undefined` — an empty string still fails validation.
  // Blank inputs must be omitted, not sent as ''.
  const submit = (v: FormValues) =>
    onSubmit({
      title: v.title,
      description: v.description?.trim() ? v.description : undefined,
      subject: v.subject?.trim() ? v.subject : undefined,
    });

  return (
    <Modal
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title={course ? `Edit ${course.title}` : 'New course'}
      size="md"
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn-primary" onClick={handleSubmit(submit)} disabled={saving}>
            {saving ? 'Saving…' : course ? 'Save changes' : 'Create course'}
          </button>
        </>
      }
    >
      <form className="space-y-4">
        <Field label="Title" required error={errors.title?.message}>
          <Input {...register('title')} placeholder="Intro to Algebra" />
        </Field>
        <Field label="Subject" error={errors.subject?.message} hint="Optional">
          <Input {...register('subject')} placeholder="Mathematics" />
        </Field>
        <Field label="Description" error={errors.description?.message} hint="Optional">
          <Textarea rows={3} {...register('description')} placeholder="What students will learn…" />
        </Field>
      </form>
    </Modal>
  );
}

function errMsg(e: unknown): string | undefined {
  if (!e) return undefined;
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.message ?? 'Something went wrong';
}
